import type { SimConfig } from '../config/simConfig';
import type { Vec3 } from '../types';
import { DT } from '../config/simConfig';
import { BTN } from './input';

// D43: charged-kick trajectory preview (render-side consumer, pure math). Mirrors applyKicks' launch
// (charge curve, per-button min/max, loft + aimElev) and the §5.3 ball model WITHOUT spin/Magnus
// (kicks launch spinless; the real ball may pick up spin later — the promised "slight variance").
// Keep the launch math in lockstep with ballActions.applyKicks / physics/ball.integrateBall.

/** Simulated flight points (sim coords, ≤ maxTicks · DT seconds), one per tick from launch. */
export function predictKickTrajectory(
  cfg: SimConfig,
  origin: Vec3,
  aimYaw: number,
  aimElev01: number,
  button: number,
  chargeTicks: number,
  conditions: keyof SimConfig['conditions'] = 'DRY',
  maxTicks = 70,
): Vec3[] {
  const k = cfg.actions.kick;
  const cond = cfg.conditions[conditions];
  const max = (button & BTN.SHOOT) !== 0 ? k.MAX.SHOOT : (button & BTN.THROUGH) !== 0 ? k.MAX.THROUGH : k.MAX.PASS;
  const min = (button & BTN.SHOOT) !== 0 ? k.SHOOT_MIN : k.KICK_MIN;
  const loft = (button & BTN.SHOOT) !== 0 ? k.LOFT.SHOOT : (button & BTN.THROUGH) !== 0 ? k.LOFT.THROUGH : k.LOFT.PASS;
  const curve = Math.pow(Math.min(chargeTicks, k.CHARGE_CAP) / k.CHARGE_CAP, k.CHARGE_EXP);
  const speed = min + (max - min) * curve;
  const elev = loft + Math.max(0, Math.min(1, aimElev01)) * k.ELEV_GAIN;
  const horiz = speed * Math.cos(elev);

  const pos = { x: origin.x, y: origin.y, z: Math.max(0, origin.z) };
  const vel = { x: horiz * Math.cos(aimYaw), y: horiz * Math.sin(aimYaw), z: speed * Math.sin(elev) };
  const out: Vec3[] = [{ ...pos }];
  for (let t = 0; t < maxTicks; t++) {
    const grounded = pos.z <= 0 && vel.z <= 0;
    const sp = Math.hypot(vel.x, vel.y, vel.z);
    let ax = 0;
    let ay = 0;
    let az = grounded ? 0 : -cfg.ball.GRAVITY;
    const kd = cfg.ball.KD_AIR * sp;
    ax -= kd * vel.x;
    ay -= kd * vel.y;
    az -= kd * vel.z;
    if (grounded) {
      ax -= cond.KF_ROLL * vel.x;
      ay -= cond.KF_ROLL * vel.y;
    }
    vel.x += ax * DT;
    vel.y += ay * DT;
    vel.z += az * DT;
    if (grounded && vel.z < 0) vel.z = 0;
    pos.x += vel.x * DT;
    pos.y += vel.y * DT;
    pos.z += vel.z * DT;
    if (pos.z < 0) {
      pos.z = 0;
      if (vel.z < 0) {
        vel.z = -cond.REST * vel.z;
        vel.x *= 1 - cfg.ball.BOUNCE_GRIP;
        vel.y *= 1 - cfg.ball.BOUNCE_GRIP;
      }
    }
    out.push({ ...pos });
    if (Math.hypot(vel.x, vel.y, vel.z) < 1.5) break; // effectively at rest — stop drawing
  }
  return out;
}

/**
 * D45 Item 3 — inverse of the loft: the aimElev01 ∈ [0,1] whose flight arrives at height `targetZ` when it
 * has travelled `distM` horizontally (for the given kick button + charge). Pure; scans the range with the
 * same §5.3 model as predictKickTrajectory (z-at-distance isn't monotone in elev — a scan beats a bisect).
 * Feeds the goal precision-aim mode's aimElev output. Returns a clamped [0,1] value.
 */
export function aimElevFor(
  cfg: SimConfig,
  distM: number,
  button: number,
  chargeTicks: number,
  targetZ: number,
  conditions: keyof SimConfig['conditions'] = 'DRY',
  steps = 48,
): number {
  const origin = { x: 0, y: 0, z: 0 };
  let bestElev = 0;
  let bestErr = Infinity;
  for (let i = 0; i <= steps; i++) {
    const elev01 = i / steps;
    const pts = predictKickTrajectory(cfg, origin, 0, elev01, button, chargeTicks, conditions, 90);
    // z where the flight first reaches horizontal distance distM (linear interp between samples)
    let zAt: number | null = null;
    for (let k = 1; k < pts.length; k++) {
      const d0 = pts[k - 1]!.x;
      const d1 = pts[k]!.x;
      if (d1 >= distM && d0 <= distM) {
        const f = d1 > d0 ? (distM - d0) / (d1 - d0) : 0;
        zAt = pts[k - 1]!.z + (pts[k]!.z - pts[k - 1]!.z) * f;
        break;
      }
    }
    if (zAt === null) continue; // this loft never reaches distM (too flat/short at low charge) — skip
    const err = Math.abs(zAt - targetZ);
    if (err < bestErr) {
      bestErr = err;
      bestElev = elev01;
    }
  }
  return Math.max(0, Math.min(1, bestElev));
}
